import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  Button,
  Stack,
  Link,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  IconButton,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Image,
} from '@chakra-ui/react';
import { useAuth } from '../../context/AuthContext';
import { useLeague } from '../../context/LeagueContext';
import { auth } from '../../lib/firebase';
import { LeagueId } from '../../types';
import ColorModeSwitcher from '../ColorModeSwitcher';

const LEAGUES: { id: LeagueId; name: string; image: string }[] = [
  { id: 'liga_mx', name: 'Liga BBVA MX', image: '/images/ligamx_logo.png' },
  { id: 'mundial_fifa', name: 'Mundial FIFA 2026', image: '/images/mundial_2026.png' },
];

const Navbar = () => {
  const { isOpen, onToggle } = useDisclosure();
  const { user, loading } = useAuth();
  const { league, setLeague } = useLeague();
  const navigate = useNavigate();
  const isAdmin = user?.email === 'htorres@teiker.mx';

  const { colorMode } = useColorMode();
  const navText = 'white';
  const navLinkColor = useColorModeValue('whiteAlpha.900', 'gray.200');
  const hoverBg = useColorModeValue('pitch.600', 'pitch.800');
  const avatarColor = useColorModeValue('white', 'gray.800');
  const leagueMenuBg = useColorModeValue('pitch.50', 'gray.700');
  const leagueImageBg = useColorModeValue('#dfe4e88a', 'white');
  const menuListBg = useColorModeValue('white', 'slate.850');
  const menuTextColor = useColorModeValue('gray.800', 'gray.100');
  const menuItemHoverBg = useColorModeValue('gray.100', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const mobileLabelColor = useColorModeValue('whiteAlpha.700', 'gray.400');
  const mobileOutlineBorder = useColorModeValue('whiteAlpha.400', 'gray.600');

  const menuListProps = {
    bg: menuListBg,
    color: menuTextColor,
    borderWidth: '1px',
    borderColor: borderColor,
    boxShadow: 'lg',
    py: 1,
  };

  const menuItemProps = {
    color: menuTextColor,
    _hover: { bg: menuItemHoverBg, color: menuTextColor },
    _focus: { bg: menuItemHoverBg, color: menuTextColor },
  };

  const currentLeague = league ? LEAGUES.find(l => l.id === league) : null;

  const handleLeagueChange = (leagueId: LeagueId) => {
    setLeague(leagueId);
    navigate('/');
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  return (
    <Box>
      <Flex
        bg={useColorModeValue('pitch.600', 'pitch.700')}
        minH={'60px'}
        py={{ base: 2 }}
        px={{ base: 4 }}
        borderBottom="3px solid"
        borderColor={useColorModeValue('pitch.800', 'pitch.900')}
        align={'center'}
      >
        <Flex
          flex={{ base: 1, md: 'auto' }}
          ml={{ base: -2 }}
          display={{ base: 'flex', md: 'none' }}
        >
          <IconButton
            onClick={onToggle}
            icon={
              <Text fontSize="xl" color={navText}>
                {isOpen ? '✕' : '☰'}
              </Text>
            }
            variant={'ghost'}
            aria-label={'Toggle Navigation'}
            p={2}
            color={navText}
            _hover={{ bg: hoverBg }}
          />
        </Flex>
        
        <Flex flex={{ base: 1 }} justify={{ base: 'center', md: 'start' }}>
          <Link
            as={RouterLink}
            to="/"
            textAlign={useColorModeValue('left', 'center')}
            fontFamily={'heading'}
            color={navText}
            _hover={{ textDecoration: 'none' }}
          >
            <Text fontSize="xl" fontWeight="bold">
              Quinielas
            </Text>
          </Link>

          <Flex display={{ base: 'none', md: 'flex' }} ml={10} align="center">
            <Stack direction={'row'} spacing={4} align="center">
              {user && league && currentLeague && (
                <Menu>
                  <MenuButton
                    as={Button}
                    variant="ghost"
                    size="sm"
                    color={navText}
                    _hover={{ bg: hoverBg, color: navText }}
                    _active={{ bg: hoverBg, color: navText }}
                    rightIcon={<Text as="span" fontSize="xs" color={navText}>▼</Text>}
                    leftIcon={
                      <Image
                        src={currentLeague.image}
                        alt={currentLeague.name}
                        boxSize="28px"
                        objectFit="contain"
                        bg={leagueImageBg}
                        borderRadius="md"
                        p={0.5}
                      />
                    }
                  >
                    <Text fontSize="sm" fontWeight="medium" noOfLines={1} maxW="140px" color={navText}>
                      {currentLeague.name}
                    </Text>
                  </MenuButton>
                  <MenuList {...menuListProps} minW="200px">
                    {LEAGUES.map((l) => (
                      <MenuItem
                        key={l.id}
                        onClick={() => handleLeagueChange(l.id)}
                        bg={l.id === league ? leagueMenuBg : undefined}
                        {...menuItemProps}
                      >
                        <HStack spacing={3}>
                          <Image
                            src={l.image}
                            alt={l.name}
                            boxSize="32px"
                            objectFit="contain"
                            bg={leagueImageBg}
                            borderRadius="md"
                            p={0.5}
                          />
                          <Text fontSize="sm" color={menuTextColor}>{l.name}</Text>
                        </HStack>
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
              {user && (
                <>
                  <Link
                    as={RouterLink}
                    to="/"
                    px={2}
                    py={1}
                    rounded={'md'}
                    color={navLinkColor}
                    _hover={{ textDecoration: 'none', bg: hoverBg, color: navText }}
                  >
                    Inicio
                  </Link>
                  {league === 'mundial_fifa' && (
                    <>
                      <Link
                        as={RouterLink}
                        to="/mundial"
                        px={2}
                        py={1}
                        rounded={'md'}
                        color={navLinkColor}
                        _hover={{ textDecoration: 'none', bg: hoverBg, color: navText }}
                      >
                        Torneo
                      </Link>
                      <Link
                        as={RouterLink}
                        to="/mundial?tab=historia"
                        px={2}
                        py={1}
                        rounded={'md'}
                        color={navLinkColor}
                        _hover={{ textDecoration: 'none', bg: hoverBg, color: navText }}
                      >
                        Historia FIFA
                      </Link>
                      <Link
                        as={RouterLink}
                        to="/reglamento"
                        px={2}
                        py={1}
                        rounded={'md'}
                        color={navLinkColor}
                        _hover={{ textDecoration: 'none', bg: hoverBg, color: navText }}
                      >
                        Reglamento
                      </Link>
                    </>
                  )}
                  <Link
                    as={RouterLink}
                    to="/rankings"
                    px={2}
                    py={1}
                    rounded={'md'}
                    color={navLinkColor}
                    _hover={{ textDecoration: 'none', bg: hoverBg, color: navText }}
                  >
                    Puntuaciones
                  </Link>
                  <Link
                    as={RouterLink}
                    to="/quiniela/quinielas-capturadas"
                    px={2}
                    py={1}
                    rounded={'md'}
                    color={navLinkColor}
                    _hover={{ textDecoration: 'none', bg: hoverBg, color: navText }}
                  >
                    Quinielas
                  </Link>
                  {isAdmin && (
                    <Menu>
                      <MenuButton
                        as={Button}
                        variant="ghost"
                        size="sm"
                        fontWeight="normal"
                        color={navLinkColor}
                        _hover={{ bg: hoverBg, color: navText }}
                        _active={{ bg: hoverBg, color: navText }}
                        rightIcon={<Text as="span" fontSize="xs">▼</Text>}
                      >
                        Administración
                      </MenuButton>
                      <MenuList {...menuListProps}>
                        <MenuItem as={RouterLink} to="/admin/matches" {...menuItemProps}>
                          Gestionar Partidos
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/admin/results" {...menuItemProps}>
                          Resultados
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/admin/ver-quinielas" {...menuItemProps}>
                          Ver Quinielas capturadas
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/admin/activar-quinielas" {...menuItemProps}>
                          Activar Quinielas
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/admin/users" {...menuItemProps}>
                          Gestionar Usuarios
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/admin/predictions" {...menuItemProps}>
                          Gestionar Pronósticos
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/admin/reset-predictions" {...menuItemProps}>
                          Reiniciar pronósticos por partido
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/admin/mundial" {...menuItemProps}>
                          Mundial FIFA 2026
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </>
              )}
            </Stack>
          </Flex>
        </Flex>

        <Stack
          flex={{ base: 1, md: 0 }}
          justify={'flex-end'}
          direction={'row'}
          spacing={6}
        >
          {!loading && (
            <>
              {!user ? (
                <>
                  <Button
                    as={RouterLink}
                    to="/login"
                    fontSize={'sm'}
                    fontWeight={400}
                    variant={'link'}
                    color={navText}
                    _hover={{ color: 'white', opacity: 0.9 }}
                  >
                    Iniciar Sesión
                  </Button>
                  <Button
                    as={RouterLink}
                    to="/register"
                    display={{ base: 'none', md: 'inline-flex' }}
                    fontSize={'sm'}
                    fontWeight={600}
                    color={'white'}
                    colorScheme="gold"
                  >
                    Registrarse
                  </Button>
                </>
              ) : (
                <Menu>
                  <MenuButton
                    as={Button}
                    rounded={'full'}
                    variant={'ghost'}
                    cursor={'pointer'}
                    minW={0}
                    color={navText}
                    _hover={{ bg: hoverBg, color: navText }}
                  >
                    <HStack>
                      <Avatar
                        size={'sm'}
                        name={user?.name || user?.displayName || user?.email || 'Usuario'}
                        src={user?.photoURL || undefined}
                        bg={user?.photoURL ? undefined : (user?.avatarBgColor || 'blue.500')}
                        color={avatarColor}
                      />
                      <Text display={{ base: 'none', md: 'flex' }} color={navText}>
                        {user?.name || user?.displayName || user?.email || 'Usuario'}
                      </Text>
                    </HStack>
                  </MenuButton>
                  <MenuList {...menuListProps}>
                    <MenuItem as={RouterLink} to="/profile" {...menuItemProps}>
                      Mi Perfil
                    </MenuItem>
                    <MenuItem onClick={handleLogout} {...menuItemProps}>
                      Cerrar Sesión
                    </MenuItem>
                    <Box
                      as="div"
                      px={3}
                      py={2}
                      borderTopWidth="1px"
                      borderColor={borderColor}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Flex align="center">
                        <ColorModeSwitcher size="sm" />
                        <Text ml={2} fontSize="sm" color={menuTextColor}>
                          {colorMode === 'light' ? 'Modo oscuro' : 'Modo claro'}
                        </Text>
                      </Flex>
                    </Box>
                  </MenuList>
                </Menu>
              )}
            </>
          )}
        </Stack>
      </Flex>

      {/* Menú móvil */}
      <Box
        display={{ base: isOpen ? 'block' : 'none', md: 'none' }}
        bg={useColorModeValue('pitch.600', 'pitch.700')}
        px={4}
        pb={4}
      >
        <Stack as={'nav'} spacing={4}>
          {user && league && currentLeague && (
            <Box pb={2} borderBottomWidth={1} borderColor={borderColor}>
              <Text fontSize="xs" color={mobileLabelColor} mb={1}>Competencia actual</Text>
              <Menu>
                <MenuButton
                  as={Button}
                  variant="outline"
                  size="sm"
                  w="full"
                  color={navText}
                  borderColor={mobileOutlineBorder}
                  _hover={{ bg: hoverBg }}
                  leftIcon={
                    <Image
                      src={currentLeague.image}
                      alt={currentLeague.name}
                      boxSize="24px"
                      objectFit="contain"
                      bg={leagueImageBg}
                      borderRadius="md"
                      p={0.5}
                    />
                  }
                >
                  {currentLeague.name}
                </MenuButton>
                <MenuList {...menuListProps} minW="200px">
                  {LEAGUES.map((l) => (
                    <MenuItem
                      key={l.id}
                      onClick={() => { handleLeagueChange(l.id); onToggle(); }}
                      bg={l.id === league ? leagueMenuBg : undefined}
                      {...menuItemProps}
                    >
                      <HStack spacing={3}>
                        <Image src={l.image} alt={l.name} boxSize="28px" objectFit="contain" bg={leagueImageBg} borderRadius="md" p={0.5} />
                        <Text fontSize="sm" color={menuTextColor}>{l.name}</Text>
                      </HStack>
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Box>
          )}
          {user && (
            <>
              <Link
                as={RouterLink}
                to="/"
                px={2}
                py={1}
                rounded={'md'}
                color={navText}
                onClick={onToggle}
                _hover={{ textDecoration: 'none', bg: hoverBg, color: navText }}
              >
                Inicio
              </Link>
              {league === 'mundial_fifa' && (
                <>
                  <Link
                    as={RouterLink}
                    to="/mundial"
                    px={2}
                    py={1}
                    rounded={'md'}
                    color={navText}
                    onClick={onToggle}
                    _hover={{ textDecoration: 'none', bg: hoverBg }}
                  >
                    Torneo
                  </Link>
                  <Link
                    as={RouterLink}
                    to="/mundial?tab=historia"
                    px={2}
                    py={1}
                    rounded={'md'}
                    color={navText}
                    onClick={onToggle}
                    _hover={{ textDecoration: 'none', bg: hoverBg }}
                  >
                    Historia FIFA
                  </Link>
                  <Link
                    as={RouterLink}
                    to="/reglamento"
                    px={2}
                    py={1}
                    rounded={'md'}
                    color={navText}
                    onClick={onToggle}
                    _hover={{ textDecoration: 'none', bg: hoverBg }}
                  >
                    Reglamento
                  </Link>
                </>
              )}
              <Link
                as={RouterLink}
                to="/rankings"
                px={2}
                py={1}
                rounded={'md'}
                color={navText}
                onClick={onToggle}
                _hover={{ textDecoration: 'none', bg: hoverBg }}
              >
                Puntuaciones
              </Link>
              <Link
                as={RouterLink}
                to="/quiniela/quinielas-capturadas"
                px={2}
                py={1}
                rounded={'md'}
                color={navText}
                onClick={onToggle}
                _hover={{ textDecoration: 'none', bg: hoverBg }}
              >
                Quinielas
              </Link>
              {isAdmin && (
                <>
                  <Text px={2} py={1} fontWeight="bold" color={navText}>Administración</Text>
                  <Link
                    as={RouterLink}
                    to="/admin/matches"
                    px={4}
                    py={1}
                    rounded={'md'}
                    color={navLinkColor}
                    onClick={onToggle}
                    _hover={{ textDecoration: 'none', bg: hoverBg, color: navText }}
                  >
                    Gestionar Partidos
                  </Link>
                  <Link
                    as={RouterLink}
                    to="/admin/results"
                    px={4}
                    py={1}
                    rounded={'md'}
                    color={navLinkColor}
                    onClick={onToggle}
                    _hover={{ textDecoration: 'none', bg: hoverBg, color: navText }}
                  >
                    Resultados
                  </Link>
                  <Link
                    as={RouterLink}
                    to="/admin/ver-quinielas"
                    px={4}
                    py={1}
                    rounded={'md'}
                    color={navLinkColor}
                    onClick={onToggle}
                    _hover={{ textDecoration: 'none', bg: hoverBg, color: navText }}
                  >
                    Ver Quinielas capturadas
                  </Link>
                  <Link
                    as={RouterLink}
                    to="/admin/activar-quinielas"
                    px={4}
                    py={1}
                    rounded={'md'}
                    color={navLinkColor}
                    onClick={onToggle}
                    _hover={{ textDecoration: 'none', bg: hoverBg, color: navText }}
                  >
                    Activar Quinielas
                  </Link>
                  <Link
                    as={RouterLink}
                    to="/admin/users"
                    px={4}
                    py={1}
                    rounded={'md'}
                    color={navLinkColor}
                    onClick={onToggle}
                    _hover={{ textDecoration: 'none', bg: hoverBg, color: navText }}
                  >
                    Gestionar Usuarios
                  </Link>
                  <Link
                    as={RouterLink}
                    to="/admin/predictions"
                    px={4}
                    py={1}
                    rounded={'md'}
                    color={navLinkColor}
                    onClick={onToggle}
                    _hover={{ textDecoration: 'none', bg: hoverBg, color: navText }}
                  >
                    Gestionar Pronósticos
                  </Link>
                  <Link
                    as={RouterLink}
                    to="/admin/reset-predictions"
                    px={4}
                    py={1}
                    rounded={'md'}
                    color={navLinkColor}
                    onClick={onToggle}
                    _hover={{ textDecoration: 'none', bg: hoverBg, color: navText }}
                  >
                    Reiniciar pronósticos por partido
                  </Link>
                  <Link
                    as={RouterLink}
                    to="/admin/mundial"
                    px={4}
                    py={1}
                    rounded={'md'}
                    color={navLinkColor}
                    onClick={onToggle}
                    _hover={{ textDecoration: 'none', bg: hoverBg, color: navText }}
                  >
                    Mundial FIFA 2026
                  </Link>
                </>
              )}
            </>
          )}
          <Box pt={2} borderTopWidth={1} borderColor={borderColor}>
            <Flex align="center" px={2}>
              <ColorModeSwitcher size="sm" />
              <Text ml={2} fontSize="sm" color={navLinkColor}>
                {colorMode === 'light' ? 'Modo oscuro' : 'Modo claro'}
              </Text>
            </Flex>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default Navbar; 